import {
  ProviderParametersFragment,
  SolarPanelParameters,
} from '../../../generated/graphql'
import { EnergyFlow, PrimaryEnergyFlow, WasteType } from '../../EnergyFlow'
import {
  createHourlyProvidedFrom,
  createHourlyFromEnvironmentFrom,
  createSecondaryHourlyFromEnvironment,
  createSecondaryHourlyWaste,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../../HourlyEnergyFlow'
import { HourlyVariables, Provider } from './Provider'

export type LimitedResourceVariables = HourlyVariables & {
  remainingDemand: PrimaryHourlyEnergyFlow | undefined
  available: number
}

type LimitedResourceProviderParameters = ProviderParametersFragment &
  Pick<SolarPanelParameters, 'isWasteSold'>

/**
 * Provides for the demand from a limited resource.
 * Excess resources are converted to waste.
 */
export abstract class LimitedResourceProvider<
  TParameters extends LimitedResourceProviderParameters,
> extends Provider<TParameters, LimitedResourceVariables> {
  private initialAvailable!: number[]

  protected initialize(): void {
    this.initialAvailable = this.getInitialAvailable()
  }

  /**
   * Allow providing waste flows even when no demands exist.
   */
  protected shouldTriggerEarlyReturn(
    combinedInputEnergyFlows: PrimaryEnergyFlow[],
  ): boolean {
    return combinedInputEnergyFlows.length === 0 && this.hourSeries.length === 0
  }

  /**
   * This implementation differs from the single type provider equivalent by accepting empty demands.
   */
  protected provide(demands: PrimaryEnergyFlow[]): EnergyFlow[] {
    const hourIndices =
      demands.length > 0
        ? demands[0].hourSeries.map((_, hour) => hour)
        : this.hourSeries[0].values.map((_, hour) => hour)

    const hourlyOutputFlows = hourIndices.map((hour) =>
      this.provideForHour(
        this.getHourlyVariables(
          hour,
          demands.map((demand) => ({
            ...demand,
            value: demand.hourSeries[hour],
          })),
        ),
      ),
    )
    if (!hourlyOutputFlows.length) {
      return []
    }

    return super.combineHourlyEnergyFlowsToEnergyFlows(hourlyOutputFlows)
  }

  /**
   * Almost the same as single type provider implementation, with the exception that it accepts empty demands.
   *
   * @param demands Electricity demand as only member in array, or empty array
   */
  protected getHourlyVariables(
    hour: number,
    demands: PrimaryHourlyEnergyFlow[],
  ): LimitedResourceVariables {
    if (demands.length > 1) {
      throw new Error(
        'Single type provider can only provider for one energy type',
      )
    }

    return {
      remainingDemand: demands.length === 1 ? demands[0] : undefined,
      outdoorTemperature: this.temperatureValues?.[hour],
      available: this.initialAvailable[hour],
    }
  }

  protected provideForHour({
    available,
    remainingDemand,
  }: LimitedResourceVariables): HourlyEnergyFlow[] {
    const flows: HourlyEnergyFlow[] = []
    let providedValue = 0

    if (remainingDemand) {
      const provided = createHourlyProvidedFrom(
        remainingDemand,
        remainingDemand.energyType,
        Math.min(remainingDemand.value, available),
      )

      const internalConsumption = createHourlyFromEnvironmentFrom(
        provided,
        provided.energyType,
        provided.value,
      )

      providedValue = provided.value
      flows.push(provided)
      flows.push(internalConsumption)
    }

    const leftForWaste = available - providedValue

    const wasted = createSecondaryHourlyWaste(
      this.id,
      this.providedEnergyTypes[0],
      leftForWaste,
      this.parameters.isWasteSold ? WasteType.Sold : WasteType.Lost,
    )

    const providedForWaste = createSecondaryHourlyFromEnvironment(
      this.id,
      this.providedEnergyTypes[0],
      leftForWaste,
    )

    flows.push(wasted)
    flows.push(providedForWaste)

    return flows
  }

  protected abstract getInitialAvailable(): number[]
}
